import { Injectable } from '@angular/core';
import { Observable, of, Subscription, timer } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import { BxLogger } from '../../../../../../core/services/bx-logger.service';
import { RepoEstimatesService } from '../../../../../../data/repositories/repo-estimates.service';
import { EstimatePlansV2Service } from '../../plans-widget/feat-estimate-plans-v2/estimate-plans-v2.service';
import {
    EstimateMeasurement,
    EstimateMeasurementApisService,
    EstimateMeasurementCategoryVM,
    EstimateMeasurementStoresService,
    EstimateMeasurementVM,
} from './domain';
import { EstimateMeasurementCalculatorService } from './estimate-measurement-calculator.service';
import { EstimateMeasurementCategoryService } from './estimate-measurement-category.service';
import { EstimateMeasurementTakeOffsService } from './estimate-measurement-take-offs.service';
import { EstimateMeasurementsDragDropService } from './estimate-measurements-drag-drop.service';

@Injectable({
    providedIn: 'root',
})
export class EstimateMeasurementAutoSaveService {
    private isAutoSaving = false;
    // @ts-ignore TS2564
    private subWatchAutoSave: Subscription;

    constructor(
        private readonly estimateMeasurementStoresService: EstimateMeasurementStoresService,
        private readonly estimateMeasurementCategoryService: EstimateMeasurementCategoryService,
        private readonly estimateMeasurementTakeOffsService: EstimateMeasurementTakeOffsService,
        private readonly estimateMeasurementCalculatorService: EstimateMeasurementCalculatorService,
        private readonly estimateMeasurementsDragDropService: EstimateMeasurementsDragDropService,
        private readonly estimateMeasurementApisService: EstimateMeasurementApisService,
        private readonly bxLogger: BxLogger,
        private readonly estimatesRepo: RepoEstimatesService,
        private readonly estimatePlansV2Service: EstimatePlansV2Service
    ) {}

    public startAutoSave() {
        if (!this.subWatchAutoSave || this.subWatchAutoSave.closed) {
            this.subWatchAutoSave = timer(0, 10000 * 1).subscribe(() => this.autoSave());
        }
    }

    public completeAutoSave() {
        this.autoSave();
        if (this.subWatchAutoSave && !this.subWatchAutoSave.closed) {
            this.subWatchAutoSave.unsubscribe();
        }
    }

    public autoSave() {
        this.saveMeasurements().subscribe((isSaved: boolean) => {
            if (isSaved) {
                this.estimatesRepo.Save();
                this.bxLogger.success('Measurements are auto saved');
            }
        });
    }

    public saveMeasurements(): Observable<boolean> {
        if (
            this.isAutoSaving ||
            this.estimateMeasurementTakeOffsService.isTakeOffUpdating ||
            this.estimateMeasurementsDragDropService.isDraging
        ) {
            return of(false);
        }

        this.isAutoSaving = true;

        const changedEstimateMeasurements = this.getChangedEstimateMeasurements();
        if (!changedEstimateMeasurements?.length) {
            this.isAutoSaving = false;
            return of(false);
        }

        const estimateId = this.estimateMeasurementStoresService.estimateId;

        return this.estimatePlansV2Service.savePlanByBreeze().pipe(
            switchMap(() => this.estimateMeasurementApisService.bulkUpsertEstimateMeasurements(estimateId, changedEstimateMeasurements)),
            map((result) => {
                const measurements = (<any>result.data).bulkUpsertEstimateMeasurements;

                if (!measurements) {
                    return false;
                }

                this.syncChangedMeasurementCategories(measurements);

                this.estimateMeasurementCalculatorService.recalculateCostingItems();

                return true;
            }),
            finalize(() => (this.isAutoSaving = false))
        );
    }

    private getChangedEstimateMeasurements(): EstimateMeasurement[] {
        const allCategories = this.estimateMeasurementStoresService.snapshotMeasurementCategories;

        // @ts-ignore TS7034
        const changedEstimateMeasurements = [];

        allCategories.forEach((item: EstimateMeasurementVM) => {
            const estimateMeasurement = this.estimateMeasurementCategoryService.mapToUpsertEstimateMeasurement(item);
            if (estimateMeasurement) {
                changedEstimateMeasurements.push(estimateMeasurement);
            }
        });

        // @ts-ignore TS7005
        return changedEstimateMeasurements;
    }

    private syncChangedMeasurementCategories(measurements: EstimateMeasurement[]) {
        const allCategories = this.estimateMeasurementStoresService.snapshotMeasurementCategories;

        measurements.forEach((changed: EstimateMeasurement) => {
            if (!changed?.parentId) {
                this.updateCategoryByEstimateMeasurement(allCategories, changed);
            } else {
                this.updateMeasurementByEstimateMeasurement(allCategories, changed);
            }
        });
    }

    private updateCategoryByEstimateMeasurement(allItems: EstimateMeasurementVM[], changed: EstimateMeasurement) {
        const index = allItems.findIndex((c) =>
            c.id ? c.id === changed.iD_EstimateMeasurement : c.order === changed.key && !c.measurements && !changed.isParent
        );
        if (index < 0) {
            return;
        }

        if (changed.isDeleted) {
            allItems.splice(index, 1);
            return;
        }

        // @ts-ignore TS2532
        const newItem: EstimateMeasurementVM = { ...allItems[index], originalMeasurement: changed };
        // @ts-ignore TS2345
        allItems.splice(index, 1, newItem);

        if (newItem.isSelected && !newItem.parentId) {
            this.estimateMeasurementStoresService.selectedMeasurement = newItem;
        }
    }

    private updateMeasurementByEstimateMeasurement(allCategories: EstimateMeasurementCategoryVM[], changed: EstimateMeasurement) {
        const category = allCategories.find((c) => c.id === changed.parentId);
        if (!category) {
            return;
        }

        // @ts-ignore TS2532
        const index = category.measurements.findIndex((m) =>
            m.id ? m.id === changed.iD_EstimateMeasurement : m.parentId === changed.parentId && m.order === changed.key
        );
        if (index < 0) {
            return;
        }

        if (changed.isDeleted) {
            // @ts-ignore TS2532
            category.measurements.splice(index, 1);
            return;
        }

        // @ts-ignore TS2532
        const newMeasurement: EstimateMeasurementVM = {
            // @ts-ignore TS2532
            ...category.measurements[index],
            originalMeasurement: changed,
            // @ts-ignore TS2532
            id: category.measurements[index].id || changed.iD_EstimateMeasurement,
        };
        // @ts-ignore TS2532
        category.measurements.splice(index, 1, newMeasurement);

        if (newMeasurement.isSelected) {
            this.estimateMeasurementStoresService.selectedMeasurement = newMeasurement;
        }
    }
}
